import type { JobTag, JobTagType } from '@seek/chalice-types';

import type { JobBadgeProps } from '../components/JobBadges/JobBadges';

const BADGE_ORDER: readonly JobTagType[] = [
  'NEW',
  'UNSEEN',
  'TOP_APPLICANT',
  'EARLY_APPLICANT',
  'EXPIRES_SOON',
] as const;

const sortBadges = (badges: JobTag[]) =>
  badges.sort(
    (a, b) => BADGE_ORDER.indexOf(a.type) - BADGE_ORDER.indexOf(b.type),
  );

const filterBadges = (badges: JobTag[]): JobTag[] => {
  if (badges.some(({ type }) => type === 'EXPIRES_SOON')) {
    return badges.filter(({ type }) => type !== 'EARLY_APPLICANT');
  }
  return badges;
};

export const getRecommendationBadges = (tags: JobTag[] | undefined) => {
  const badges: JobBadgeProps[] = [];
  const analyticsBadges: JobTagType[] = [];

  const { badgesTags, viewedTag } = tags
    ?.filter((tag): tag is JobTag => Boolean(tag.label))
    .reduce<{
      badgesTags: JobTag[];
      viewedTag: JobTag | undefined;
    }>(
      (acc, tag) => {
        if (tag.type === 'VIEWED') {
          acc.viewedTag = tag;
        } else if (BADGE_ORDER.includes(tag.type)) {
          acc.badgesTags.push(tag);
        }
        return acc;
      },
      { badgesTags: [], viewedTag: undefined },
    ) || { badgesTags: [], viewedTag: undefined };

  filterBadges(sortBadges(badgesTags)).forEach((tag) => {
    switch (tag.type) {
      // New and Unseen are mutually exclusive, we don't have to account for both¸
      // https://api.jobapi.net/docs/mordor/tags
      case 'NEW':
      case 'UNSEEN':
        badges.push({
          label: tag.label,
          tone: 'positive',
          data: {
            automation: 'newBadge',
          },
        });
        analyticsBadges.push(tag.type);
        break;
      case 'EXPIRES_SOON':
        badges.push({
          label: tag.label,
          tone: 'critical',
          data: {
            automation: 'expiresSoonBadge',
          },
        });
        analyticsBadges.push(tag.type);
        break;
      case 'EARLY_APPLICANT':
        badges.push({
          label: tag.label,
          tone: 'info',
          data: {
            automation: 'earlyApplicantBadge',
          },
        });
        analyticsBadges.push(tag.type);
        break;
      case 'TOP_APPLICANT':
        badges.push({
          label: tag.label,
          tone: 'promote',
          data: {
            automation: 'topApplicantBadge',
          },
        });
        analyticsBadges.push(tag.type);
        break;
    }
  });

  const viewedLabel = viewedTag?.label || '';
  if (viewedLabel) {
    analyticsBadges.push(viewedTag!.type);
  }

  return {
    badges,
    viewedLabel,
    analyticsBadges,
  };
};
